/**
 * 元码科技导航字段映射
 * @see GET /api/nav/list?type_id=1
 * item: { id, title, sub_title, description, link, nav_list, status, sort, ... }
 */

import { navMegaMenus } from '@/data/navMenus'
import { disabledSolutionNavIds, getSolutionNavPath, solutions } from '@/data/solutions'
import { parseNavLink, splitNavPath } from './navigation'

function normalizeSolutionNavPath(path) {
  const match = (path || '').match(/^\/solutions\/([^/?#]+)/)
  if (match) {
    return getSolutionNavPath(match[1])
  }
  return path
}

function normalizeNavTitle(text) {
  return (text || '').replace(/[\s/|／]/g, '').toLowerCase()
}

function isSolutionSubNavPath(path) {
  const { path: pathname, hash } = splitNavPath(path || '')
  if (pathname === '/solutions' && hash) return true
  return /^\/solutions\/[^/?#]+$/.test(pathname)
}

function findSolutionByNavTitle(title) {
  const normalized = normalizeNavTitle(title)
  if (!normalized) return null

  const keywordRules = [
    { keywords: ['教培机构'], id: 'edu-management' },
    { keywords: ['德育教务', '文档收集', '评比平台'], id: 'moral-education' },
    { keywords: ['在线考试', '刷题'], id: 'online-exam' },
    { keywords: ['ai安防', '安防监控'], id: 'ai-security' }
  ]

  const keywordMatch = keywordRules.find(rule =>
    rule.keywords.some(keyword => normalized.includes(normalizeNavTitle(keyword)))
  )
  if (keywordMatch) {
    return solutions.find(solution => solution.id === keywordMatch.id) || null
  }

  return solutions.find(solution => {
    const solutionTitle = normalizeNavTitle(solution.title)
    return (
      solutionTitle === normalized ||
      normalized.includes(solutionTitle) ||
      solutionTitle.includes(normalized)
    )
  }) || null
}

/** API 子项链接常配置为首页，按标题回退到本地解决方案锚点 */
function resolveSolutionChildNavPath(child, parentPath) {
  const parentBase = splitNavPath(parentPath).path
  if (parentBase !== '/solutions') return null

  const { path, external } = parseNavLink(child.link)
  if (external) return null

  const normalizedPath = normalizeSolutionNavPath(path)
  if (isSolutionSubNavPath(normalizedPath)) {
    const solutionId = getSolutionIdFromNavPath(normalizedPath)
    if (solutionId && disabledSolutionNavIds.includes(solutionId)) return null
    return normalizedPath
  }

  const matched = findSolutionByNavTitle(child.title)
  if (!matched || disabledSolutionNavIds.includes(matched.id)) return null
  return getSolutionNavPath(matched.id)
}

function megaMenuHasItems(megaMenu) {
  if (!megaMenu || !Array.isArray(megaMenu.columns) || !megaMenu.columns.length) {
    return false
  }
  return megaMenu.columns.some(column => Array.isArray(column.items) && column.items.length)
}

export function resolveNavPath(item) {
  return parseNavLink(item.link).path
}

export function resolveNavHref(item) {
  return parseNavLink(item.link).href
}

function filterEnabledNavList(list) {
  if (!Array.isArray(list)) return []
  return list
    .filter(item => item.status === 1 || item.status === undefined)
    .sort((a, b) => (a.sort || 0) - (b.sort || 0))
}

function getSolutionIdFromNavPath(path) {
  const match = (path || '').match(/^\/solutions\/([^/?#]+)/)
  return match ? match[1] : null
}

function isDisabledSolutionNavItem(child) {
  const matched = findSolutionByNavTitle(child.title)
  if (matched && disabledSolutionNavIds.includes(matched.id)) return true

  const { path } = parseNavLink(child.link)
  const solutionId = getSolutionIdFromNavPath(normalizeSolutionNavPath(path))
  return Boolean(solutionId && disabledSolutionNavIds.includes(solutionId))
}

function isPlaceholderHomePath(path) {
  const { path: pathname, hash } = splitNavPath(path || '')
  return pathname === '/' && !hash
}

function mapMegaItems(list, parentPath = '/') {
  return filterEnabledNavList(list).map(child => {
    const title = child.title || ''
    const desc = child.description || child.sub_title || ''

    if (isDisabledSolutionNavItem(child)) {
      return {
        title,
        desc,
        disabled: true,
        isNew: false
      }
    }

    const rawLink = (child.link || '').trim()
    // 无链接或仅占位首页：不可跳转，也不应参与高亮
    if (!rawLink || rawLink === '#' || rawLink === '/') {
      return {
        title,
        desc,
        disabled: true,
        isNew: false
      }
    }

    const { path, href, external } = parseNavLink(child.link)
    let normalizedPath = external ? path : normalizeSolutionNavPath(path)

    if (!external) {
      const solutionPath = resolveSolutionChildNavPath(child, parentPath)
      if (solutionPath) {
        normalizedPath = solutionPath
      }
    }

    // 解决方案子项常被后台配成首页，标题又匹配不到本地方案时，视为不可跳转
    const parentBase = splitNavPath(parentPath).path
    if (!external && parentBase === '/solutions' && isPlaceholderHomePath(normalizedPath)) {
      return {
        title,
        desc,
        disabled: true,
        isNew: false
      }
    }

    return {
      title,
      desc,
      path: normalizedPath,
      href: external ? href : normalizedPath,
      external,
      isNew: false
    }
  })
}

export function buildMegaMenuFromNav(item) {
  const children = filterEnabledNavList(item.nav_list)
  if (!children.length) return null

  const parentPath = resolveNavPath(item)
  const hasNested = children.some(child => child.nav_list && child.nav_list.length)

  if (hasNested) {
    return {
      columns: children.map(group => ({
        title: group.title,
        items: mapMegaItems(
          group.nav_list && group.nav_list.length ? group.nav_list : [group],
          parentPath
        )
      }))
    }
  }

  const items = mapMegaItems(children, parentPath)
  if (items.length <= 5) {
    return {
      columns: [{
        title: item.sub_title || '快速导航',
        items
      }]
    }
  }

  const mid = Math.ceil(items.length / 2)
  return {
    columns: [
      { title: item.sub_title || '导航', items: items.slice(0, mid) },
      { title: item.description || '更多', items: items.slice(mid) }
    ]
  }
}

export function mapNavItem(item) {
  const { path, href, external } = parseNavLink(item.link)
  const megaMenu = buildMegaMenuFromNav(item)

  return {
    id: item.id,
    name: item.title || '',
    path,
    href,
    external,
    megaMenuKey: `nav-${item.id}`,
    megaMenu
  }
}

export function mapNavList(list) {
  if (!Array.isArray(list)) return []

  return filterEnabledNavList(list)
    .filter(item => !item.parent_id)
    .map(mapNavItem)
}

export function getFlatMegaItems(navItem) {
  if (!navItem || !navItem.megaMenu) return []
  return navItem.megaMenu.columns.flatMap(col => col.items)
}

export function findNavItemByKey(navItems, key) {
  return navItems.find(item => item.megaMenuKey === key || item.path === key)
}

/** 是否有可展示的下拉/二级菜单（API 导航以接口数据为准，本地导航可回退 navMenus） */
export function hasNavSubmenu(navItem, { navFromApi = false } = {}) {
  if (megaMenuHasItems(navItem && navItem.megaMenu)) {
    return true
  }
  if (!navFromApi && navItem && navItem.path && navMegaMenus[navItem.path]) {
    return true
  }
  return false
}

export function resolveMegaMenu(navItem, key, { navFromApi = false } = {}) {
  if (megaMenuHasItems(navItem && navItem.megaMenu)) {
    return navItem.megaMenu
  }
  if (!navFromApi) {
    if (navItem && navItem.path && navMegaMenus[navItem.path]) {
      return navMegaMenus[navItem.path]
    }
    if (key && navMegaMenus[key]) {
      return navMegaMenus[key]
    }
  }
  return null
}

/** 是否为简单下拉（单列、无丰富描述），使用紧凑下拉而非全宽 Mega Menu */
export function isSimpleMegaMenu(menu) {
  if (!menu || !Array.isArray(menu.columns) || menu.columns.length !== 1) {
    return false
  }

  const column = menu.columns[0]
  const items = column && column.items
  if (!Array.isArray(items) || !items.length || items.length > 8) {
    return false
  }

  const hasRichDesc = items.some(item => (item.desc || '').trim().length > 0)
  return !hasRichDesc
}
